import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, CirclePause, CircleStop, Hammer, Play, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { inputClass, primaryButtonClass } from "@/components/Panel";
import { Badge, Card, type StatusTone } from "@/components/ui";
import { postingErrorMessage } from "@/features/production/postingsApi";
import {
  clockOperation,
  liveMinutes,
  reportClocked,
  workbenchKeys,
  workbenchOperationQuery,
  workbenchQueueQuery,
  workCenterOptionsQuery,
  type ClockAction,
  type ClockState,
  type QueueRow,
  type WorkbenchOperation,
} from "@/features/production/workbench";
import { useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { useFormat } from "@/lib/format";
import { useCan } from "@/lib/session";

// Operator workbench (PX-MFG-130, docs/design/workbench.md): pick the work
// center, take an operation from its queue, clock setup / run / pause /
// stop and report good / scrap — the minutes come from the clock.
// Round-trips: work centers (cached), queue (1), operation (1); clock and
// report are one RPC each; queue and operation refresh every 30 s.

interface Search {
  wc?: string | undefined;
  op?: string | undefined;
}

const UUID = /^[0-9a-f-]{36}$/i;
const WC_KEY = "patronix.workbench.wc";

export const Route = createFileRoute("/_app/manufacturing/workbench")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const s: Search = {};
    if (typeof raw.wc === "string" && UUID.test(raw.wc)) s.wc = raw.wc;
    if (typeof raw.op === "string" && UUID.test(raw.op)) s.op = raw.op;
    return s;
  },
  component: WorkbenchPage,
});

const stateTone: Record<ClockState, StatusTone> = { idle: "neutral", setup: "violet", run: "success", pause: "warning", stop: "neutral" };

function WorkbenchPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { company } = useActiveCompany();
  const companyId = company?.id ?? "";
  const centers = useQuery(workCenterOptionsQuery(companyId));
  const wc = search.wc ?? "";
  const queue = useQuery(workbenchQueueQuery(companyId, wc));

  // The last work center comes back (one operator, one station).
  useEffect(() => {
    if (search.wc || !centers.data?.length) return;
    let saved: string | null = null;
    try {
      saved = localStorage.getItem(WC_KEY);
    } catch {
      saved = null;
    }
    const pick = centers.data.find((c) => c.id === saved) ?? centers.data[0];
    if (pick) void navigate({ search: { wc: pick.id }, replace: true });
  }, [centers.data, search.wc, navigate]);

  const setWc = (id: string) => {
    try {
      localStorage.setItem(WC_KEY, id);
    } catch {
      // Remembering is a convenience only.
    }
    void navigate({ search: { wc: id }, replace: true });
  };

  return (
    <div className="flex min-h-0 grow flex-col gap-4 pb-6" data-testid="workbench">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col">
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("wb.title")}</h1>
          <span className="text-[13px] text-ink-muted">{t("wb.intro")}</span>
        </div>
        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          {t("wb.workCenter")}
          <select value={wc} onChange={(e) => setWc(e.target.value)} className={cn(inputClass, "h-10 min-w-[240px] text-[14px]")}>
            {!wc && <option value="">—</option>}
            {(centers.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} — {c.name}
              </option>
            ))}
          </select>
        </label>
      </header>

      {centers.data && centers.data.length === 0 && <Card className="p-6 text-[13px] text-ink-muted">{t("wb.noCenters")}</Card>}

      <div className="grid min-h-0 grow gap-4 lg:grid-cols-[minmax(320px,2fr)_minmax(0,3fr)]">
        <section aria-label={t("wb.queue")} className="flex min-h-0 flex-col gap-2">
          <h2 className="text-[13px] font-semibold text-ink-soft">
            {t("wb.queue")} {queue.data && <span className="tabular text-ink-muted">· {queue.data.length}</span>}
          </h2>
          {!queue.data && wc ? (
            <div className="flex flex-col gap-2" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-[92px] animate-pulse rounded-2xl bg-surface-muted" />
              ))}
            </div>
          ) : queue.data && queue.data.length === 0 ? (
            <Card className="p-6 text-[13px] text-ink-muted">{t("wb.empty")}</Card>
          ) : (
            <ul className="flex flex-col gap-2 overflow-y-auto">
              {(queue.data ?? []).map((r) => (
                <li key={r.id}>
                  <QueueCard row={r} selected={r.id === search.op} onSelect={() => void navigate({ search: { wc, op: r.id }, replace: true })} />
                </li>
              ))}
            </ul>
          )}
        </section>
        <section aria-label={t("wb.operation")} className="min-w-0">
          {search.op ? (
            <OperationPanel key={search.op} id={search.op} companyId={companyId} wc={wc} />
          ) : (
            <Card className="flex h-full min-h-[240px] items-center justify-center p-6 text-[13px] text-ink-muted">{t("wb.pick")}</Card>
          )}
        </section>
      </div>
    </div>
  );
}

function QueueCard({ row: r, selected, onSelect }: { row: QueueRow; selected: boolean; onSelect: () => void }) {
  const { t } = useTranslation();
  const tr = t as unknown as (k: string) => string;
  const fmt = useFormat();
  const d = r.uom_decimals ?? 0;
  const done = Number(r.good_qty);
  const planned = Number(r.planned_qty);
  const pct = planned > 0 ? Math.min(100, Math.round((100 * done) / planned)) : 0;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? "true" : undefined}
      className={cn(
        "flex w-full flex-col gap-1.5 rounded-2xl border bg-surface p-3 text-left transition-colors hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-focus",
        selected ? "border-accent ring-1 ring-accent" : "border-border",
      )}
    >
      <span className="flex items-center gap-2">
        <span className="font-mono text-[13px] font-medium">{r.order_number}</span>
        <span className="text-xs text-ink-muted">· {t("wb.op", { seq: r.seq })}</span>
        <span className="grow" />
        {r.short_line_count > 0 && (
          <Badge tone="danger">
            <AlertTriangle size={11} className="mr-1" aria-hidden="true" />
            {t("wb.short")}
          </Badge>
        )}
        {r.my_state !== "idle" && r.my_state !== "stop" && <Badge tone={stateTone[r.my_state]}>{tr(`wb.state.${r.my_state}`)}</Badge>}
        {r.active_operators > 0 && (
          <span className="inline-flex items-center gap-0.5 text-xs text-ink-muted" title={t("wb.activeOperators", { count: r.active_operators })}>
            <Users size={12} aria-hidden="true" />
            {r.active_operators}
          </span>
        )}
      </span>
      <span className="truncate text-[13px]">
        <span className="font-mono">{r.item_code}</span> {r.item_description}
      </span>
      <span className="truncate text-xs text-ink-muted">{r.description}</span>
      <span className="flex items-center gap-2 text-xs">
        <span className="h-1.5 grow overflow-hidden rounded-full bg-surface-muted">
          <span className="block h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
        </span>
        <span className="tabular whitespace-nowrap text-ink-soft">
          {fmt.qtyIn(done, d)} / {fmt.qtyIn(planned, d)} {r.uom_code ?? ""}
        </span>
        {r.start_date && <span className="tabular whitespace-nowrap text-ink-muted">{fmt.day(r.start_date)}</span>}
      </span>
      {r.previous_good_qty !== null && (
        <span className="text-xs text-ink-muted">{t("wb.previousDone", { qty: fmt.qtyIn(r.previous_good_qty, d) })}</span>
      )}
    </button>
  );
}

function OperationPanel({ id, companyId, wc }: { id: string; companyId: string; wc: string }) {
  const { t } = useTranslation();
  const q = useQuery(workbenchOperationQuery(id));
  // The confirmation lives here: the body below remounts on every reload (its
  // clock and form start fresh), and the message must survive the reload that
  // follows the report.
  const [done, setDone] = useState<string | null>(null);
  if (q.isPending) return <div className="h-[420px] animate-pulse rounded-2xl bg-surface-muted" aria-hidden="true" />;
  if (!q.data) return <Card className="p-6 text-[13px] text-ink-muted">{t("notFound.body")}</Card>;
  return (
    <OperationBody
      key={`${q.data.id}-${q.dataUpdatedAt}`}
      op={q.data}
      loadedAt={q.dataUpdatedAt}
      companyId={companyId}
      wc={wc}
      done={done}
      setDone={setDone}
    />
  );
}

function OperationBody({
  op,
  loadedAt,
  companyId,
  wc,
  done,
  setDone,
}: {
  op: WorkbenchOperation;
  loadedAt: number;
  companyId: string;
  wc: string;
  /** The last report's confirmation (kept by OperationPanel across reloads). */
  done: string | null;
  setDone: (v: string | null) => void;
}) {
  const { t } = useTranslation();
  const tr = t as unknown as (k: string, o?: Record<string, unknown>) => string;
  const fmt = useFormat();
  const queryClient = useQueryClient();
  const canPost = useCan("production.posting.post");
  const [now, setNow] = useState(() => Date.now());
  const [good, setGood] = useState("");
  const [scrap, setScrap] = useState("");
  const [minutes, setMinutes] = useState("");
  const [note, setNote] = useState("");
  const [stop, setStop] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const active = op.my_state === "setup" || op.my_state === "run";
  const d = op.order.uom_decimals ?? 0;
  const live = liveMinutes(op, now, loadedAt);

  useEffect(() => {
    if (!active) return;
    const h = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(h);
  }, [active]);

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: workbenchKeys.operation(op.id) }),
      queryClient.invalidateQueries({ queryKey: workbenchKeys.queue(companyId, wc) }),
      queryClient.invalidateQueries({ queryKey: ["production"] }),
      queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
    ]);

  const clock = useMutation({
    mutationFn: (a: ClockAction) => clockOperation(op.id, a),
    onMutate: () => {
      setError(null);
      setDone(null);
    },
    onSuccess: refresh,
    onError: (e) => setError(postingErrorMessage(t, e)),
  });
  const report = useMutation({
    mutationFn: () => reportClocked(op.id, { good, scrap, minutes: minutes.trim() || null, note: note.trim() || null, stop }),
    onMutate: () => {
      setError(null);
      setDone(null);
    },
    onSuccess: async (r) => {
      setDone(t("wb.reported", { number: r.number, minutes: fmt.qty(String(r.minutes)) }));
      setGood("");
      setScrap("");
      setMinutes("");
      setNote("");
      setStop(false);
      await refresh();
    },
    onError: (e) => setError(postingErrorMessage(t, e)),
  });

  const actions: { a: ClockAction; icon: typeof Play; tone: string; show: boolean }[] = [
    { a: "setup", icon: Hammer, tone: "bg-(--status-violet-bg) text-(--status-violet-fg)", show: op.status !== "done" && op.my_state !== "setup" },
    { a: "run", icon: Play, tone: "bg-(--status-success-bg) text-(--status-success-fg)", show: op.status !== "done" && op.my_state !== "run" },
    { a: "pause", icon: CirclePause, tone: "bg-(--status-warning-bg) text-(--status-warning-fg)", show: active },
    { a: "stop", icon: CircleStop, tone: "bg-surface-muted text-ink-soft", show: active || op.my_state === "pause" },
  ];

  return (
    <Card className="flex flex-col gap-4 p-4" data-testid="workbench-operation">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex min-w-0 grow flex-col">
          <span className="font-mono text-xs text-ink-muted">
            {op.order.number} · {t("wb.op", { seq: op.seq })} · {op.work_center_code}
          </span>
          <h2 className="truncate text-[18px] font-semibold leading-6">
            {op.order.item_code} — {op.order.item_description}
          </h2>
          <span className="text-[13px] text-ink-soft">{op.description}</span>
        </div>
        <Badge tone={stateTone[op.my_state]}>{tr(`wb.state.${op.my_state}`)}</Badge>
      </div>

      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Fact label={t("wb.done")} value={`${fmt.qtyIn(op.good_qty, d)} / ${fmt.qtyIn(op.order.planned_qty, d)} ${op.order.uom_code ?? ""}`} />
        <Fact label={t("wb.scrap")} value={fmt.qtyIn(op.scrap_qty, d)} />
        <Fact label={t("wb.planned")} value={t("wb.minutesPlanned", { setup: fmt.qty(String(op.planned_setup_minutes)), run: fmt.qty(String(op.planned_run_minutes)) })} />
        <Fact label={t("wb.clocked")} value={t("wb.minutes", { value: fmt.qty(live.total.toFixed(1)) })} strong />
      </dl>

      {canPost && (
        <div className="flex flex-wrap gap-2" role="group" aria-label={t("wb.clock")}>
          {actions
            .filter((x) => x.show)
            .map(({ a, icon: Icon, tone }) => (
              <button
                key={a}
                type="button"
                disabled={clock.isPending}
                onClick={() => clock.mutate(a)}
                className={cn("inline-flex h-12 min-w-[120px] items-center justify-center gap-2 rounded-xl px-4 text-[15px] font-semibold disabled:opacity-60", tone)}
              >
                <Icon size={18} aria-hidden="true" />
                {tr(`wb.action.${a}`)}
              </button>
            ))}
        </div>
      )}

      {canPost && op.status !== "done" && (
        <form
          className="flex flex-col gap-3 rounded-xl border border-border p-3"
          aria-label={t("wb.report")}
          onSubmit={(e) => {
            e.preventDefault();
            report.mutate();
          }}
        >
          <h3 className="text-[13px] font-semibold">{t("wb.report")}</h3>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <label className="flex flex-col gap-1 text-xs text-ink-muted">
              {t("wb.good")}
              <input inputMode="decimal" value={good} onChange={(e) => setGood(e.target.value)} className={cn(inputClass, "h-11 text-[16px]")} />
            </label>
            <label className="flex flex-col gap-1 text-xs text-ink-muted">
              {t("wb.scrap")}
              <input inputMode="decimal" value={scrap} onChange={(e) => setScrap(e.target.value)} className={cn(inputClass, "h-11 text-[16px]")} />
            </label>
            <label className="flex flex-col gap-1 text-xs text-ink-muted">
              {t("wb.minutesLabel")}
              <input
                inputMode="decimal"
                value={minutes}
                placeholder={live.total.toFixed(1)}
                onChange={(e) => setMinutes(e.target.value)}
                className={cn(inputClass, "h-11 text-[16px]")}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-ink-muted">
              {t("wb.note")}
              <input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} className={cn(inputClass, "h-11")} />
            </label>
          </div>
          <p className="text-xs text-ink-muted">{t("wb.minutesHint")}</p>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-[13px]">
              <input type="checkbox" checked={stop} onChange={(e) => setStop(e.target.checked)} className="size-4 accent-(--accent)" />
              {t("wb.stopAfter")}
            </label>
            <span className="grow" />
            <button type="submit" disabled={report.isPending || (!good && !scrap && !minutes && live.total === 0)} className={primaryButtonClass}>
              {t("wb.reportButton")}
            </button>
          </div>
        </form>
      )}

      {error && (
        <p role="alert" className="text-[13px] text-(--status-danger-fg)">
          {error}
        </p>
      )}
      {done && (
        <p role="status" className="text-[13px] text-(--status-success-fg)">
          {done}
        </p>
      )}

      {op.is_last && (
        <p className="rounded-lg bg-surface-muted px-3 py-2 text-[13px] text-ink-soft">
          {t("wb.lastOperation")}{" "}
          <Link to="/manufacturing/production-orders" search={{ status: "all", po: op.order.id } as never} className="font-medium text-accent-ink hover:underline">
            {t("wb.openOrder")} →
          </Link>
        </p>
      )}

      {op.materials.length > 0 && (
        <section className="flex flex-col gap-1.5">
          <h3 className="text-[13px] font-semibold">{t("wb.materials")}</h3>
          <table className="tabular w-full text-[13px]" aria-label={t("wb.materials")}>
            <thead>
              <tr className="text-left text-xs text-ink-muted">
                <th className="py-1 font-medium">{t("wb.item")}</th>
                <th className="py-1 text-right font-medium">{t("wb.required")}</th>
                <th className="py-1 text-right font-medium">{t("wb.issued")}</th>
                <th className="py-1 pl-3 font-medium">{t("wb.method")}</th>
              </tr>
            </thead>
            <tbody>
              {op.materials.map((m) => (
                <tr key={m.line_no} className="border-t border-border-subtle">
                  <td className="py-1.5">
                    <span className="font-mono">{m.item_code}</span> <span className="text-ink-muted">{m.item_description}</span>
                  </td>
                  <td className="py-1.5 text-right">
                    {fmt.qtyIn(m.required_base_qty, m.uom_decimals ?? 0)} {m.uom_code ?? ""}
                  </td>
                  <td className="py-1.5 text-right">{fmt.qtyIn(m.issued_base_qty, m.uom_decimals ?? 0)}</td>
                  <td className="py-1.5 pl-3 text-xs text-ink-muted">{tr(`wb.issue.${m.issue_method}`)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {op.events.length > 0 && (
        <section className="flex flex-col gap-1.5">
          <h3 className="text-[13px] font-semibold">{t("wb.events")}</h3>
          <ul className="flex flex-col gap-0.5 text-xs text-ink-soft">
            {op.events.map((e, i) => (
              <li key={i} className="tabular">
                {fmt.dateTime(e.at)} · {tr(`wb.event.${e.kind}`)} · {e.user_name ?? "—"}
              </li>
            ))}
          </ul>
        </section>
      )}
    </Card>
  );
}

function Fact({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-xl border border-border px-3 py-2">
      <dt className="text-[11px] font-medium uppercase tracking-[0.04em] text-ink-faint">{label}</dt>
      <dd className={cn("tabular text-[15px]", strong ? "font-semibold" : "font-medium")}>{value}</dd>
    </div>
  );
}
